import React, { createContext, useContext, ReactNode } from "react";
import { useAuth } from "./supabase-auth";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { useCheckout } from "BEDROCK/pages/checkout/checkout-provider";
type Role = "admin" | "user";

type AuthContextType = {
  client: SupabaseClient | undefined;
  session: Session | null;
  isLoading: boolean;
  userID: string | null;
  role: Role | null;
  signOut: () => Promise<void>;
  isUserAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = (props: AuthProviderProps) => {
  const {
    session,
    isLoading,
    role,
    userID,
    signOut: authSignOut,
    isUserAuthenticated,
    client,
  } = useAuth();

  const { clearCheckout } = useCheckout();

  const signOut = async (): Promise<void> => {
    clearCheckout();
    await authSignOut();
  };

  return (
    <AuthContext.Provider
      value={{
        client,
        session,
        isLoading,
        role,
        signOut,
        isUserAuthenticated,
        userID,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
